import { useEffect, useState, type Key, type ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ANY, type FilterDef } from "@/lib/filters"
import { useApi, type ApiResponse, type Nav } from "@/lib/hooks"
import type { Page } from "@shared/types"

// How many ~37px table rows will fit, assuming a 250px gap below / above the table
const numRows = Math.max(5, Math.floor((window.innerHeight - 250) / 37))

export type Col<Row> = { header: string; cell: (row: Row) => ReactNode }
export type TabDef<Row> = {
  id: string
  label: string
  path: string
  detailBase: string
  fetchPage: (query: Record<string, string>) => Promise<ApiResponse<Page<Row>>>
  keyOf: (row: Row) => Key
  cols: Col<Row>[]
  filters?: FilterDef[]
}

function buildUrl(
  path: string,
  params: URLSearchParams,
  changes: Record<string, string | null>,
  resetPage: boolean,
) {
  const next = new URLSearchParams(params)
  for (const [k, v] of Object.entries(changes)) {
    if (v == null || v === "") next.delete(k)
    else next.set(k, v)
  }
  if (resetPage) next.delete("page")
  const qs = next.toString()
  return `${path}${qs ? `?${qs}` : ""}`
}

function SelectFilter({
  def,
  value,
  onChange,
}: {
  def: FilterDef
  value: string
  onChange: (v: string) => void
}) {
  return (
    <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? "" : v)}>
      <SelectTrigger className="w-48">
        <SelectValue placeholder={def.label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{def.label}</SelectItem>
        {def.options?.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function TextFilter({
  def,
  value,
  onCommit,
}: {
  def: FilterDef
  value: string
  onCommit: (v: string) => void
}) {
  const [local, setLocal] = useState(value)
  useEffect(() => setLocal(value), [value])
  useEffect(() => {
    if (local === value) return
    const id = setTimeout(() => onCommit(local), 300)
    return () => clearTimeout(id)
  }, [local, value, onCommit])
  return (
    <Input
      className="w-48"
      value={local}
      placeholder={def.label}
      onChange={(e) => setLocal(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit(local)
      }}
      onBlur={() => onCommit(local)}
    />
  )
}

export function PagedTable<Row>({
  tab,
  params,
  nav,
}: {
  tab: TabDef<Row>
  params: URLSearchParams
  nav: Nav
}) {
  const page = Math.max(1, Number(params.get("page")) || 1)
  const { data, err } = useApi(() => tab.fetchPage({
    pageSize: String(numRows),
    ...Object.fromEntries(params),
  }), [
    tab.id,
    params.toString(),
  ])

  const setFilter = (key: string, value: string) => {
    if ((params.get(key) ?? "") === value) return
    nav(buildUrl(tab.path, params, { [key]: value }, true))
  }

  return (
    <div className="space-y-4">
      {tab.filters && tab.filters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {tab.filters.map((f) =>
            f.kind === "select" ? (
              <SelectFilter
                key={f.key}
                def={f}
                value={params.get(f.key) ?? ""}
                onChange={(v) => setFilter(f.key, v)}
              />
            ) : (
              <TextFilter
                key={f.key}
                def={f}
                value={params.get(f.key) ?? ""}
                onCommit={(v) => setFilter(f.key, v)}
              />
            ),
          )}
        </div>
      )}

      {err ? (
        <p className="py-4 text-sm text-red-600">{err}</p>
      ) : !data ? (
        <p className="py-4 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                {tab.cols.map((c) => (
                  <TableHead key={c.header}>{c.header}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((row) => (
                <TableRow
                  key={tab.keyOf(row)}
                  className="cursor-pointer"
                  onClick={() => nav(`${tab.detailBase}/${tab.keyOf(row)}`)}
                >
                  {tab.cols.map((c) => (
                    <TableCell key={c.header}>{c.cell(row)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {data.total == null
                ? `Page ${data.page}`
                : `Page ${data.page} of ${Math.max(1, Math.ceil(data.total / data.pageSize))} · ${data.total} total`}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={data.page <= 1}
                onClick={() =>
                  nav(
                    buildUrl(
                      tab.path,
                      params,
                      { page: page - 1 > 1 ? String(page - 1) : null },
                      false,
                    ),
                  )
                }
              >
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!data.hasNext}
                onClick={() =>
                  nav(buildUrl(tab.path, params, { page: String(page + 1) }, false))
                }
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
